import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  otherGroupsFormEstablishmentTypes,
  schoolFormEstablishmentTypes,
  tourTypes,
} from '../registrationFormComponents/data';

const ExportData = ({
  onExport,
  adminData,
  allowedSections = ['school', 'other-group', 'experience'],
  specificSchoolTypes = null,
  specificOtherGroupTypes = null,
  showAllDataOption = false,
}) => {
  const { t } = useTranslation();
  const isInitialRender = useRef(true);

  // Expanded states for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    scolaire: true,
    autreGroupe: true,
    experience: true,
  });

  // Selected export options
  const [selectedOptions, setSelectedOptions] = useState({
    allData: false,
    schoolTypes: [],
    otherGroupTypes: [],
    experienceTypes: [],
  });

  // Check admin permissions and allowed sections
  const hasSchoolPermission = adminData?.assignedRegistrationTypes?.includes('school');
  const hasOtherGroupPermission = adminData?.assignedRegistrationTypes?.includes('other-group');

  // Memoize the allowed sections string for stable comparison
  const allowedSectionsKey = useMemo(() => allowedSections.join(','), [allowedSections]);

  // Determine what sections should be shown based on both permissions and allowed sections
  const shouldShowSchool = useMemo(
    () => hasSchoolPermission && allowedSections.includes('school'),
    [hasSchoolPermission, allowedSectionsKey],
  );

  const shouldShowOtherGroup = useMemo(
    () => hasOtherGroupPermission && allowedSections.includes('other-group'),
    [hasOtherGroupPermission, allowedSectionsKey],
  );

  const shouldShowExperience = useMemo(
    () => hasSchoolPermission && allowedSections.includes('experience'),
    [hasSchoolPermission, allowedSectionsKey],
  );

  // Get filtered school types based on specificSchoolTypes
  const availableSchoolTypes = useMemo(() => {
    if (!specificSchoolTypes) return schoolFormEstablishmentTypes;
    return schoolFormEstablishmentTypes.filter((type) => specificSchoolTypes.includes(type.value));
  }, [specificSchoolTypes]);

  // Get filtered other group types based on specificOtherGroupTypes
  const availableOtherGroupTypes = useMemo(() => {
    if (!specificOtherGroupTypes) return otherGroupsFormEstablishmentTypes;
    return otherGroupsFormEstablishmentTypes.filter((type) =>
      specificOtherGroupTypes.includes(type.value),
    );
  }, [specificOtherGroupTypes]);

  // Experience types are always shown in full when experience section is allowed
  const availableExperienceTypes = tourTypes;

  // Reset selected options when permissions or allowed sections change
  useEffect(() => {
    // Skip the initial render to avoid clearing default values
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    const updatedOptions = {
      allData: false,
      schoolTypes: shouldShowSchool ? [] : [],
      otherGroupTypes: shouldShowOtherGroup ? [] : [],
      experienceTypes: shouldShowExperience ? [] : [],
    };

    setSelectedOptions(updatedOptions);

    // Notify parent about changes
    if (onExport) {
      onExport(updatedOptions);
    }
  }, [shouldShowSchool, shouldShowOtherGroup, shouldShowExperience, onExport]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleCheckboxChange = (category, value) => {
    const updatedOptions = { ...selectedOptions };

    if (category === 'allData') {
      updatedOptions.allData = !selectedOptions.allData;

      // If "Export all data" is checked, select all available options
      if (!selectedOptions.allData) {
        // Only add school types if they should be shown
        if (shouldShowSchool) {
          updatedOptions.schoolTypes = availableSchoolTypes.map((type) => type.value);
        }

        // Only add experience types if they should be shown
        if (shouldShowExperience) {
          updatedOptions.experienceTypes = availableExperienceTypes.map((type) => type.value);
        }

        // Only add other group types if they should be shown
        if (shouldShowOtherGroup) {
          updatedOptions.otherGroupTypes = availableOtherGroupTypes.map((type) => type.value);
        }
      } else {
        // If unchecking "Export all data", deselect all
        updatedOptions.schoolTypes = [];
        updatedOptions.otherGroupTypes = [];
        updatedOptions.experienceTypes = [];
      }
    } else if (category === 'allSchoolTypes') {
      // Toggle all school types if they should be shown
      if (shouldShowSchool) {
        if (
          availableSchoolTypes.every((type) => selectedOptions.schoolTypes.includes(type.value))
        ) {
          updatedOptions.schoolTypes = [];
        } else {
          updatedOptions.schoolTypes = availableSchoolTypes.map((type) => type.value);
        }
      }
    } else if (category === 'allOtherGroupTypes') {
      // Toggle all other group types if they should be shown
      if (shouldShowOtherGroup) {
        if (
          availableOtherGroupTypes.every((type) =>
            selectedOptions.otherGroupTypes.includes(type.value),
          )
        ) {
          updatedOptions.otherGroupTypes = [];
        } else {
          updatedOptions.otherGroupTypes = availableOtherGroupTypes.map((type) => type.value);
        }
      }
    } else if (category === 'allExperienceTypes') {
      // Toggle all experience types if they should be shown
      if (shouldShowExperience) {
        if (
          availableExperienceTypes.every((type) =>
            selectedOptions.experienceTypes.includes(type.value),
          )
        ) {
          updatedOptions.experienceTypes = [];
        } else {
          updatedOptions.experienceTypes = availableExperienceTypes.map((type) => type.value);
        }
      }
    } else if (category === 'schoolTypes') {
      // Only allow school type selection if they should be shown
      if (shouldShowSchool) {
        updatedOptions.schoolTypes = selectedOptions.schoolTypes.includes(value)
          ? selectedOptions.schoolTypes.filter((item) => item !== value)
          : [...selectedOptions.schoolTypes, value];
      }
    } else if (category === 'otherGroupTypes') {
      // Only allow other group type selection if they should be shown
      if (shouldShowOtherGroup) {
        updatedOptions.otherGroupTypes = selectedOptions.otherGroupTypes.includes(value)
          ? selectedOptions.otherGroupTypes.filter((item) => item !== value)
          : [...selectedOptions.otherGroupTypes, value];
      }
    } else if (category === 'experienceTypes') {
      // Only allow experience type selection if they should be shown
      if (shouldShowExperience) {
        updatedOptions.experienceTypes = selectedOptions.experienceTypes.includes(value)
          ? selectedOptions.experienceTypes.filter((item) => item !== value)
          : [...selectedOptions.experienceTypes, value];
      }
    }

    // Update the "all data" checkbox state based on if all items are selected
    // All data is only true if:
    // 1. We're showing the "Export all data" option (meaning we have access to full data)
    // 2. All ORIGINAL (unfiltered) types are selected for sections that should be shown
    // 3. No filtering is applied (we have access to all types, not just a subset)

    const isShowingFullSchoolTypes =
      !specificSchoolTypes || specificSchoolTypes.length === schoolFormEstablishmentTypes.length;
    const isShowingFullOtherGroupTypes =
      !specificOtherGroupTypes ||
      specificOtherGroupTypes.length === otherGroupsFormEstablishmentTypes.length;

    const allOriginalSchoolSelected =
      shouldShowSchool &&
      isShowingFullSchoolTypes &&
      schoolFormEstablishmentTypes.every((type) => updatedOptions.schoolTypes.includes(type.value));

    const allOriginalOtherGroupsSelected =
      shouldShowOtherGroup &&
      isShowingFullOtherGroupTypes &&
      otherGroupsFormEstablishmentTypes.every((type) =>
        updatedOptions.otherGroupTypes.includes(type.value),
      );

    const allOriginalExperienceSelected =
      shouldShowExperience &&
      availableExperienceTypes.every((type) => updatedOptions.experienceTypes.includes(type.value));

    // All data is only true if:
    // 1. The "Export all data" option is available (showAllDataOption is true)
    // 2. All original/full types are selected for ALL sections that should be shown
    // 3. No filtering is applied (we have full access to all types)
    updatedOptions.allData =
      showAllDataOption &&
      (shouldShowSchool ? allOriginalSchoolSelected : true) &&
      (shouldShowOtherGroup ? allOriginalOtherGroupsSelected : true) &&
      (shouldShowExperience ? allOriginalExperienceSelected : true);

    setSelectedOptions(updatedOptions);

    // Notify parent component about the selection change
    if (onExport) {
      onExport(updatedOptions);
    }
  };

  // Get selection state (none, some, all)
  const getSelectionState = (items, selectedItems) => {
    if (!items || !selectedItems) return 'none';

    const selectedCount = items.filter((item) => selectedItems.includes(item.value)).length;

    if (selectedCount === 0) return 'none';
    if (selectedCount === items.length) return 'all';
    return 'some';
  };

  // Button indicator component with checkmark, dash, or empty
  const ButtonIndicator = ({ state = 'none', onClick }) => (
    <div
      className={`w-5 h-5 ${state !== 'none' ? 'bg-blue-10' : 'bg-blue-200'} rounded-md cursor-pointer flex items-center justify-center`}
      onClick={(e) => {
        e.stopPropagation(); // Prevent triggering the section toggle
        if (onClick) onClick(e);
      }}>
      {state === 'all' && (
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {state === 'some' && <div className="w-2 h-0.5 bg-white rounded-full"></div>}
    </div>
  );

  // Section header component
  const SectionHeader = ({ title, expanded, sectionId, items, selectedItems }) => {
    const selectionState = getSelectionState(items, selectedItems);

    return (
      <button
        onClick={() => toggleSection(sectionId)}
        className="w-full flex items-center justify-between p-3 text-left font-medium">
        <div className="flex items-center">
          <svg
            className={`w-4 h-4 mr-2 transition-transform ${expanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span>{title}</span>
        </div>
        <ButtonIndicator
          state={selectionState}
          onClick={() => {
            if (sectionId === 'scolaire') {
              handleCheckboxChange('allSchoolTypes');
            } else if (sectionId === 'autreGroupe') {
              handleCheckboxChange('allOtherGroupTypes');
            } else if (sectionId === 'experience') {
              handleCheckboxChange('allExperienceTypes');
            }
          }}
        />
      </button>
    );
  };

  // Option item component
  const OptionItem = ({ label, category, value }) => {
    const isSelected = selectedOptions[category]?.includes(value) || false;

    return (
      <div className="relative">
        <div className="absolute left-8 right-3 border-b border-gray-200"></div>
        <div className="flex items-center justify-between p-3 pl-8 relative z-10">
          <span>{label}</span>
          <ButtonIndicator
            state={isSelected ? 'all' : 'none'}
            onClick={() => handleCheckboxChange(category, value)}
          />
        </div>
      </div>
    );
  };

  // Check if any sections should be shown
  const hasSectionsToShow = shouldShowSchool || shouldShowOtherGroup || shouldShowExperience;

  return (
    <div className="w-full flex flex-col space-y-4 bg-blue-50 p-4 rounded-lg">
      {/* Show a message if no sections should be displayed */}
      {!hasSectionsToShow ? (
        <div className="bg-white rounded-xl overflow-hidden shadow-sm p-4 text-center">
          <p className="text-gray-600">No export options available for the current selection.</p>
          <p className="text-gray-500 text-sm mt-2">
            The current filter doesn't match your permissions or no sections are allowed.
          </p>
        </div>
      ) : (
        <>
          {/* Export all data option - only show if explicitly enabled */}
          {showAllDataOption && (
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <label
                    className="flex items-center space-x-3 cursor-pointer"
                    onClick={() => handleCheckboxChange('allData')}>
                    <span className="text-gray-700">{t('exportData.allDataOption')}</span>
                  </label>
                  <ButtonIndicator
                    state={selectedOptions.allData ? 'all' : 'none'}
                    onClick={() => handleCheckboxChange('allData')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* SCOLAIRE - Only show if should be displayed */}
          {shouldShowSchool && availableSchoolTypes.length > 0 && (
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <SectionHeader
                title="SCOLAIRE"
                expanded={expandedSections.scolaire}
                sectionId="scolaire"
                items={availableSchoolTypes}
                selectedItems={selectedOptions.schoolTypes}
              />

              {expandedSections.scolaire && (
                <div>
                  {availableSchoolTypes.map((type) => (
                    <OptionItem
                      key={type.value}
                      label={type.label}
                      category="schoolTypes"
                      value={type.value}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AUTRE GROUPE - Only show if should be displayed */}
          {shouldShowOtherGroup && availableOtherGroupTypes.length > 0 && (
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <SectionHeader
                title="AUTRE GROUPE"
                expanded={expandedSections.autreGroupe}
                sectionId="autreGroupe"
                items={availableOtherGroupTypes}
                selectedItems={selectedOptions.otherGroupTypes}
              />

              {expandedSections.autreGroupe && (
                <div>
                  {availableOtherGroupTypes.map((type) => (
                    <OptionItem
                      key={type.value}
                      label={type.label}
                      category="otherGroupTypes"
                      value={type.value}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Experience - Only show if should be displayed */}
          {shouldShowExperience && availableExperienceTypes.length > 0 && (
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <SectionHeader
                title="Experience reservable"
                expanded={expandedSections.experience}
                sectionId="experience"
                items={availableExperienceTypes}
                selectedItems={selectedOptions.experienceTypes}
              />

              {expandedSections.experience && (
                <div>
                  {availableExperienceTypes.map((type) => (
                    <OptionItem
                      key={type.value}
                      label={type.label}
                      category="experienceTypes"
                      value={type.value}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExportData;
